/// <reference path="../global.d.ts" />
/*
 * Copyright (c) 2016- 2021 19Labs Inc.
 * All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
import { time } from 'console';
import { prototype } from 'events';
import { ClinicInternal, SensorInternal } from '../internal';
import { Clinic, PatientProfile, IntakeData,Location, MeasurementData, MeasurementWithOptionalTimestamp, Sensor, SensorMeasurementData, SignUpInfo, LoginInfo, UpdateUserInfo, User, SensorTypes, ScreenName, HealthHistoryData, DocumentData, GenericMeasurement, BloodPressureMeasurement, PulseOximeterData, PulseOxMeasurement, ThermometerMeasurement, GlucometerMeasurement, WeightScaleData, WeightScaleMeaurement, SpirometerMeasurement, MultifunctionMeasurement, PdfFileData, PdfDataSummary, PrescriptionText, PrescriptionTextData, MeasurementFileDataSummary } from '../types';

// export all the types for documentation
export * from '../types'

export interface GaleEventTypes {
    /** Called when the user changes the system language. */
    languagechange: null

    /** Called when the user fills out an intake form. */
    intake: null

    /** Called when a new sesnor measurement is available. */
    sensormeasurementdata: SensorMeasurementData

    /** Called when an external authentication login JSON is received */
    externallogin: JSON

    /** Called when a sensor measurement is complete. */
    sensordata: null

    /** Called when the user logs in or out. */
    loginchange: null

    /** Called when a login request succeeeds. */
    loginsuccess: null

    /** Called when a login request fails. */
    loginerror: null

    /** Called when a new account is created. */
    signupsuccess: null

    /** Called when creating a new account has failed, with the reason for failure. */
    signuperror: string

    /** Called when a patient session has started or ended */
    sessionchange: null

    /** Called when a zoom call has ended */
    callend: null

    /** Called when a zoom call has started */
    callstart: null

    /** Called when an error is thrown from the API, with the error message */
    apierror: null

}
export type EventType = keyof GaleEventTypes

export class GaleEvent<Type extends EventType> extends Event {
    readonly data: GaleEventTypes[Type]
    constructor(type: EventType, data?: GaleEventTypes[Type]) {
        super(type)
        this.data = data!
    }
}

class SignupError extends Error {
    data: any
    constructor(data: any) {
        super(`Signup error ` + JSON.stringify(data))
        this.data = data
    }
}

let simulatorWindow: Window = window

const hex = (view: DataView) =>
    Array(view.byteLength / 4).fill('0').map((a, i) => view.getUint32(i * 4).toString(16).padStart(8, '0')).join('')

const sha256 = async (str: string) => {
    const buffer = new TextEncoder().encode(str)
    const hash = await crypto.subtle.digest("SHA-256", buffer)
    return hex(new DataView(hash))
}

async function connectToBridgeOrSimulator({ apiKey, simulatorPort }: { apiKey: string, simulatorPort?: number }): Promise<boolean> {
    if (!Reflect.has(window, 'NineteenGaleAPI') && simulatorPort) {
        simulatorWindow = window.open(`http://localhost:${simulatorPort}/simulator/index.html`, '19labs-simulator')!
        return true
    }
    const nonce = Math.random().toString(36).substring(7)
    const hash = await sha256(nonce + apiKey)
    NineteenGaleAPI.requestMessagePort(nonce, hash)
    return true
}

let nextID = 0


export class GaleAPIBridge extends EventTarget {
    private port: Promise<MessagePort>
    private key: string
    /**
     * @param options
     * @param options.apiKey The 19Labs API key.
     * @param options.simulatorPort A TCP port for running the 19Gale Simulator.
     */
    constructor(options: { apiKey: string, simulatorPort?: number }) {
        super()
        const { apiKey, simulatorPort } = options
        const self = this
        this.key = apiKey;
        this.port = new Promise<MessagePort>(resolve => {
            window.addEventListener('message', (e: MessageEvent) => {
                console.log({ e })
                if (e.data !== 'gale:port')
                    return

                const port = Array.from(e.ports)[0] as MessagePort
                port.addEventListener('message', (e: MessageEvent) => {
                    const eventFromBridge = JSON.parse(e.data)
                    console.info({ eventFromBridge })
                    var data = eventFromBridge.data;
                    if (eventFromBridge.type === 'sensormeasurementdata') {
                        data = this.adjustMeasurementData(eventFromBridge.data)
                    }
                    self.dispatchEvent(new GaleEvent(eventFromBridge.type, data))
                })
                port.start()
                resolve(port)
            })
            connectToBridgeOrSimulator({ apiKey, simulatorPort })
        })
    }

    /**
        Listen to a system event from Gale.
     */
    listen<T extends EventType>(type: T, listener: (event: GaleEvent<T>) => any) {
        super.addEventListener(type, listener as EventListener)
    }

    unlisten<T extends EventType>(type: T, listener: (event: GaleEvent<T>) => any) {
        super.removeEventListener(type, listener as EventListener)
    }

    /** @hidden */
    addEventListener(event: string, handler: EventListener) {
        super.addEventListener(event, handler)
    }

    /** @hidden */
    removeEventListener(event: string, handler: EventListener) {
        super.removeEventListener(event, handler)
    }

    /** @hidden */
    dispatchEvent<T extends EventType>(e: GaleEvent<T>) {
        return super.dispatchEvent(e)
    }

    private adjustMeasurementData(data: any): any {
        console.log("adjust measurement" + JSON.stringify(data))
        if (data.unit) {
            if (data.unit === 'lbs') {
                if (data.lbs) {
                    data.weight = parseInt(data.lbs)
                }
            }
        }
        return data;
    }

    private async sendCommand<Response = void>(method: string, ...args: any[]) {
        const port = await this.port
        console.log({ port })
        const nonce = Math.random().toString(36).substring(7)
        const hash = await sha256(nonce + this.key)
        port.postMessage(JSON.stringify({ method, args, nonce, hash }))
    }

    private async sendCommandWithResponse<R = string>(method: string, ...args: any[]) {
        const port = await this.port
        const nonce = Math.random().toString(36).substring(7)
        const cmdID = (++nextID).toString()
        const hash = await sha256(nonce + this.key)
        port.postMessage(JSON.stringify({ method, args, cmdID, nonce, hash }))
        return await new Promise<R>((resolve, reject) => {
            const listener = (e: MessageEvent) => {
                const message = JSON.parse(e.data) as { response: R, cmdID: string }
                if (message.cmdID === cmdID) {
                    resolve(message.response)
                    port.removeEventListener("message", listener)
                }
            }
            port.addEventListener('message', listener)


        })
    }

    private async waitForAnyEvent(...events: EventType[]): Promise<GaleEvent<any>> {
        const cleanup = [] as (() => void)[]
        const response = await Promise.race(events.map((e, i) => new Promise<any>(resolve => {
            this.addEventListener(e, resolve)
            cleanup.push(() => this.removeEventListener(e, resolve))
        })))

        cleanup.forEach(c => c())
        return response
    }

    /**
        @return the current user, or null if not logged in.
     */
    async getCurrentUser(): Promise<User | null> {
        const user = JSON.parse(await this.sendCommandWithResponse<string>('getCurrentUser'))
        console.log("user value " + JSON.stringify(user))
        if (user == null || user === "null") {
            console.log("user is null " + user);
            return null
        }
        return user as User;
    }

    /**
        @return the current language code, e.g. "en" or "es".
     */
    async getCurrentLanguage() {
        return await this.sendCommandWithResponse('getCurrentLanguage')
    }

    /**
        @return the current intake data, as filled in with the intake form.
     */
    async getIntake():Promise<IntakeData> {
        const data =  await this.sendCommandWithResponse('getIntakeData') 
        return JSON.parse(data) as IntakeData
    }


    /**
    @return the device serial id
     */
    async getName() {
        return await this.sendCommandWithResponse('getName')
    }



    /**
    @return the device serial id
     */
    async getSerial() {
        return await this.sendCommandWithResponse('getSerial')
    }

 

     /**
    @return the device location
     */
    async getLocation():Promise<Location> {
        const stringLocation= await this.sendCommandWithResponse('getLocation')
        return JSON.parse(stringLocation) as Location
    }

    /**
        @return all known sensor measurements for the current session.
     */
    async getSensorData(startSessionTime?: number): Promise<MeasurementData[]> {
        const data = JSON.parse(await this.sendCommandWithResponse<string>('getCurrentSensorData'))
        const sensorCategoryMap: any = {
            weight_scale: "Weight Scale",
            thermometer: "Thermometer",
            glucometer: 'Glucometer',
            bloodpressure: 'Blood Pressure Monitor',
            camera: 'Camera',
            otoscope: 'Otoscope',
            pulseoximeter: 'Pulse Oximeter',
            Stethoscope: 'Stethoscope'
        }
        var flat: MeasurementWithOptionalTimestamp[] = []
        for (const key in sensorCategoryMap) {
            const dataArray: any[] = data[key]
            if (dataArray) {
                dataArray.forEach((entry) => {
                    entry.type = sensorCategoryMap[key]
                    entry = this.adjustMeasurementData(entry);
                    if (startSessionTime) {
                        if (entry.timecomparitor > startSessionTime) {
                            flat.push(entry)
                        }
                    } else {
                        flat.push(entry)
                    }

                })
            }
        }

        const sorted = flat.sort((a: MeasurementWithOptionalTimestamp, b: MeasurementWithOptionalTimestamp) => a.timecomparitor - b.timecomparitor);
        return sorted
    }

    /**
        Attempts a login given a user and a password.
        
        @param username
        @param password
        @return the current user if successful, throws an error otherwise.
    */
    async login(info: LoginInfo) {
        const { email, password } = info
        this.sendCommand('login', { username: email, password })
        const event = await this.waitForAnyEvent('loginsuccess', 'loginerror')
        switch (event.type) {
            case 'loginsuccess':
                return await this.getCurrentUser()
            case 'loginerror':
                throw new Error('Login failed');
        }
    }

    /**
        Log outs from gale.
    */
    logout() {
        return this.sendCommand('logout')
    }

    /**
        Signs up a new user.
    */
    async signUp(info: SignUpInfo) {
        await this.sendCommand('signUp', info)
        const event = await this.waitForAnyEvent('signupsuccess', 'signuperror')
        switch (event.type) {
            case 'signupsuccess':
                return
            case 'signuperror':
                throw new SignupError(event.data);
        }
    }

    /** Saves the intake data. The format of the intake data is not defined. */
    async updateIntake(intake: object) {
        await this.sendCommand('updateIntake', intake)
    }

    /**
        Returns a list of clinics, as defined in the Gale configuration.
    */
    async getClinics(): Promise<Clinic[]> {
        const internal = await this.sendCommandWithResponse<ClinicInternal[]>('getClinics')
        return internal.map(ci => ({
            type: ci.clinicType as Clinic["type"],
            name: ci.displayname,
            logo: `data:image/jpeg;base64,${ci.logoembed}`,
            lastModified: new Date(ci.lastmodified),
            contactNumber: ci.contactnumber,
            sipNumber: ci.sipnumber,
            description: ci.shortdescription,
            zoomNumber: ci.zoomnumber,
            requiresIntake: ci.requiresintake
        }))
    }

    /**
        Returns a list of patient profiles registered to the current user.
     */
    async getPatientProfiles(): Promise<PatientProfile[]> {
        return await this.sendCommandWithResponse<PatientProfile[]>('getPatientProfiles')
    }

    /**
        Returns a list of available sensors, as defined in the Gale DMS.
    */
    async getSensors(): Promise<Sensor[]> {
        const internal = await this.sendCommandWithResponse<SensorInternal[]>('getSensors')
        return internal.map(si => ({
            type: si.type,
            name: si.name,
            logo: si.logoembed ? `data:image/jpeg;base64,${si.logoembed}` : null,
        }))
    }

    /**
        Shows a sensor page of a given type.
        @see {@link BuiltinSensorTypes}.
    */
    async showSensor(type: string) {
        const sensors = await this.getSensors()
        if (!sensors.find(s => s.type === type)) {
            return false
        }
        this.navigate('sensor', type)
        return true
    }

    /**
        Instructs Gale to navigate to a particular native page.
        @param name the name of the screen to navigate to.
        @param arg An optional argument for the screen. Can be the sensor type when showing a sensor,
        or the clinic name when showing a clinic.
    */
    navigate(name: ScreenName, arg?: string) {
        if (name === 'logout') {
            this.logout()
            return
        }
        this.sendCommand('goto', name, arg);
        simulatorWindow.focus()
    }

    /**
        Switch to a previously created patient profile.
        @param patientName the full name of the patient
        @param dateOfBirth the date of birth of the patient as recorded
     */
    switchPatientProfile(profile: PatientProfile) {
        const { patientName, dateOfBirth } = profile
        console.log("SWITCH profile " + JSON.stringify(profile) + "  " + dateOfBirth.toISOString().split('T')[0])
        this.sendCommand('switchPatientProfile', {
            patientName, dateOfBirth:
                dateOfBirth.toISOString().split('T')[0]
        })
    }

    /**
        Saves the current session data to the patient profile.
     */
    saveCurrentPatientProfile() {
        this.sendCommand('saveCurrentPatientProfile')
    }

    /**
    * Produces email popup to send patient profile pdf summary
    */
    submitHealthData() {
        this.sendCommand('submitHealthData')
    }

    /**
     * Print the current page
     */
    print() {
        this.sendCommand('print')
    }

    /**
     * Send an email with an attached pdf of a summary of the patient profile
     * @param recipients A list of email addresses that the email will be sent to
     * @param customBody The content of the email
     */
    sendSummaryEmail(recipients: string, customBody: string) {
        this.sendCommand('sendSummaryEmail', { recipients, customBody })
    }

    /**
     * 
     * @param {HealthHistoryData} healthHistory 
     * e.g. '{"allergies":"peanuts","medications":"tylenol","conditions":"asthma","medicalHistory":"The patient's medical history"}'
     */
    setIntakeHealthHistory(healthHistory: HealthHistoryData) {
        this.sendCommand('setIntakeHealthHistory', { healthHistory: healthHistory.toString() })
    }

    addExternalDocument(document: DocumentData) {
        const { type, url, source, timestamp } = document
        if (timestamp) {
            this.sendCommand('addExternalDocument', { "type": type, "url": url, "source": source, "timestamp": timestamp })
            return
        }
        this.sendCommand('addExternalDocument', { "type": type, "url": url, "source": source })
    }

    /**
     * 
     * Creates a generic measurement treated as if it was made by an external application
     */

    setMeasurement(data: GenericMeasurement) {
        const { name, value, units, device, timestamp } = data
        if (timestamp) {
            this.sendCommand('setMeasurement', { "name": name, "data": value, "unit": units, "device": device, "timestamp": timestamp })
            return
        }
        this.sendCommand('setMeasurement', { "name": name, "data": value, "unit": units, "device": device })
    }


    /**
     * 
     * @returns An object containing metedata for all pdf files stored on the device.
                Each file object within the pdf files JSON has a uri attribute which can be used to reference the file using getPdfFileByUri
     */
    async getPdfData(): Promise<PdfDataSummary> {
        return JSON.parse(await this.sendCommandWithResponse('getPdfData'))
    }

    /**
     * 
     * @returns A JSON object containing the text from all PDFs, as well as whether it is left or right aligned.
     */
    async getPrescriptionText(): Promise<PrescriptionText> {
      return JSON.parse(await this.sendCommandWithResponse('getPrescriptionText'))
    }

    /**
    * @returns An object contatining metedata for all measurement files stored on the device.
    *          Each file object within the measurement files JSON has a uri attribute which can be used to reference the file using getMeasurementFileByUri
    */
    async getMeasurementFileData(): Promise<MeasurementFileDataSummary> {
        return JSON.parse(await this.sendCommandWithResponse('getMeasurementFileData'))
    }

    /**
     * Takes a file uri as a reference, exposes the relevant file to the captive webpage and returns a reference to the newly exposed file.
     * The reference will take the form of https://{captivewebpageurl}/public/{filename} and the reference can be used as if the file were a part of the webpages directory
     * @param uri The uri that references a measurement file. The uri can be obtained from getMeasurementFileData
     * @returns Returns a string that can be used to reference the relevant file.
     */
    async getMeasurementFileByUri(uri: string) {
        return await this.sendCommandWithResponse('getMeasurementFileByUri', { "uri": uri })
    }

    /**
     * Takes a file uri as a reference, exposes the relevant file to the captive webpage and returns a reference to the newly exposed file.
     * The reference will take the form of https://{captivewebpageurl}/public/{filename} and the reference can be used as if the file were a part of the webpages directory
     * @param uri The uri that references a Pdf file. The uri can be obtained from getPdfFileData
     * @returns Returns a string that can be used to reference the relevant file.
     */
    async getPdfByUri(uri: string) {
        return await this.sendCommandWithResponse('getPdfByUri', { "uri": uri })
    }

    /**
     * 
     * @returns An object containing metadata of all prescriptions if it exists
     */

    async getPrescriptionData(): Promise<PdfFileData[]> {
        const pdfData = await this.getPdfData()
        const prescriptionData = pdfData.patientPrescriptions
        return prescriptionData ? prescriptionData : []
    }

    /**
     * 
     * @returns An object containing metadata of all prescriptions if it exists
     */

    async getPatientVisitSummaryData(): Promise<PdfFileData[]> {
        const pdfData = await this.getPdfData()
        const patientVisitSummaryData = pdfData.patientVisitSummaries
        return patientVisitSummaryData ? patientVisitSummaryData : []
    }
    /**
     * 
     * @param {string} note The note to be added to the patient profile 
     */
    setNote(note: string) {
        this.sendCommand('setNote', { note })
    }

    /**
     * 
     * @param {string} user 
     * @param {string} profile 
     */
    authenticate(user: string, profile: string) {
        this.sendCommand('authenticate', { user, profile })
    }

    /**
     * End a current session
     */
    async endSession() {
        this.sendCommand('endSessions')
    }

    /**
     * All of the below methods have a similar function, they create a manual measurement of
     * the type specified by the method using measurement data provided
     * @param data The relevant measurement data
     */

    addBloodPressureData(data: BloodPressureMeasurement) {
        const { pulse, highPressure, lowPressure, timestamp } = data
        if (timestamp) {
            this.sendCommand('addBloodPressureData', { highPressure, lowPressure, pulse, timestamp })
            return
        }
        this.sendCommand('addBloodPressureData', { highPressure, lowPressure, pulse })
    }

    addPulseOxData(data: PulseOxMeasurement) {
        let { oxygen, pulse, perfusion, respirationRate, timestamp } = data
        if (!respirationRate) {
            respirationRate = ""
        }
        if (timestamp) {
            this.sendCommand('addPulseOxData', { oxygen, pulse, perfusion, respirationRate, timestamp })
            return
        }
        this.sendCommand('addPulseOxData', { oxygen, pulse, perfusion, respirationRate })
    }

    addThermometerData(data: ThermometerMeasurement) {
        const { temperature, unit, timestamp } = data
        if (timestamp) {
            this.sendCommand('addThermometerData', { temperature, unit, timestamp })
            return
        }
        this.sendCommand('addThermometerData', { temperature, unit })
    }

    addGlucometerData(data: GlucometerMeasurement) {
        const { unit, bloodSugar, timestamp } = data
        if (timestamp) {
            this.sendCommand('addGlucometerData', { unit, bloodSugar, timestamp })
            return
        }
        this.sendCommand('addGlucometerData', { unit, bloodSugar })
    }

    addWeightScaleData(data: WeightScaleMeaurement) {
        let { unit, weight, bmi, timestamp } = data
        if (!bmi) {
            bmi = ""
        }
        if (timestamp) {
            this.sendCommand('addWeightScaleData', { unit, weight, bmi, timestamp })
            return
        }
        this.sendCommand('addWeightScaleData', { unit, weight, bmi })
    }

    addSpirometerData(data: SpirometerMeasurement) {
        const { label, unit, value, timestamp } = data
        if (timestamp) {
            this.sendCommand('addSpirometerData', { label, unit, value, timestamp })
            return
        }
        this.sendCommand('addSpirometerData', { label, unit, value })
    }

    addMultifunctionData(data: MultifunctionMeasurement) {
        const { label, unit, value, timestamp } = data
        if (timestamp) {
            this.sendCommand('addMultifunctionData', { label, unit, value, timestamp })
            return
        }
        this.sendCommand('addMultifunctionData', { label, unit, value })
    }
}
